import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import ConfirmDialog from "../../../components/ui/ConfirmDialog";
import { useToast } from "../../../components/ui/useToast";
import { useAutosave } from "../../../hooks/useAutosave";
import { getErrorMessage } from "../../../utils/apiError";
import { cx } from "../../../utils/classNames";
import {
  findMissingAnswers,
  invalidatePerformance,
  mapServerErrors,
  PERF_KEYS,
  scrollToField,
} from "../performanceUtils";
import EvaluationForm from "../form/EvaluationForm";
import MissingAnswers from "./MissingAnswers";
import ScoreBar from "./ScoreBar";
import ui from "../../../components/ui/ui.module.css";

// Editable form with 2s autosave and submit.
// Used for the employee's self-assessment and the evaluator's ratings.
//   initialResponses: the saved draft to start from
//   save(responses)  -> Promise<EvaluationDetails>  (PUT; whole object)
//   submit()         -> Promise                     (POST)
//   showScores:      evaluator only (live scores come back from each save)
const EvaluationEditor = ({
  evaluation,
  initialResponses,
  save,
  submit,
  showScores = false,
  selfResponses,
  submitLabel,
  confirmTitle,
  confirmMessage,
  successMessage,
  onSubmitted,
}) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const sections = evaluation.sections;

  const [draft, setDraft] = useState(initialResponses ?? {});
  const [scores, setScores] = useState(evaluation.scores);
  const [errors, setErrors] = useState([]);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const autosave = useAutosave(async (responses) => {
    const details = await save(responses);
    if (details?.scores) setScores(details.scores);
    queryClient.setQueryData(PERF_KEYS.details(evaluation.id), details);
  }, 2000);

  const handleChange = (next) => {
    setDraft(next);
    setErrors([]);
    autosave.schedule(next);
  };

  const missingRatings = findMissingAnswers(sections, draft).filter((m) =>
    m.message.endsWith("rating is required"),
  );

  const showErrors = (list) => {
    setErrors(list);
    scrollToField(list[0]?.fieldId);
  };

  const submitMutation = useMutation({
    mutationFn: submit,
    onSuccess: () => {
      toast.success(successMessage);
      invalidatePerformance(queryClient);
      onSubmitted?.();
    },
    onError: (error) => {
      setIsConfirmOpen(false);
      const status = error?.response?.status;
      const message = error?.response?.data?.message;

      if (status === 409) {
        toast.error("This evaluation was changed elsewhere. Reloading…");
        invalidatePerformance(queryClient);
      } else if (status === 400 && Array.isArray(message)) {
        showErrors(mapServerErrors(sections, message));
      } else {
        toast.error(getErrorMessage(error, "Unable to submit."));
      }
    },
  });

  const handleSubmitClick = async () => {
    const saved = await autosave.flush();
    if (!saved) {
      toast.error(
        "Couldn't save your latest changes. Check your connection and try again.",
      );
      return;
    }

    const missing = findMissingAnswers(sections, draft);
    if (missing.length) {
      showErrors(missing);
      return;
    }
    setIsConfirmOpen(true);
  };

  return (
    <>
      {showScores ? (
        <ScoreBar
          scores={scores}
          saveStatus={autosave.status}
          onRetry={autosave.flush}
        />
      ) : (
        autosave.status !== "idle" && (
          <ScoreBar
            scores={null}
            saveStatus={autosave.status}
            onRetry={autosave.flush}
          />
        )
      )}

      <MissingAnswers errors={errors} />

      <EvaluationForm
        sections={sections}
        responses={draft}
        onChange={handleChange}
        selfResponses={selfResponses}
      />

      <div className={cx(ui.card, ui.cardPadded, ui.pageHeader)}>
        <span className={ui.hint}>
          {missingRatings.length
            ? `${missingRatings.length} item(s) left to rate. Your answers save automatically.`
            : "All items rated. Your answers save automatically."}
        </span>
        <button
          type="button"
          className={cx(ui.button, ui.primary)}
          onClick={handleSubmitClick}
          disabled={
            submitMutation.isPending ||
            (showScores && missingRatings.length > 0)
          }
        >
          <i className="bi bi-send"></i>
          {submitLabel}
        </button>
      </div>

      {isConfirmOpen && (
        <ConfirmDialog
          title={confirmTitle}
          message={confirmMessage}
          confirmLabel={submitLabel}
          isPending={submitMutation.isPending}
          onConfirm={() => submitMutation.mutate()}
          onClose={() => setIsConfirmOpen(false)}
        />
      )}
    </>
  );
};

export default EvaluationEditor;
